<template>
  <div class="discovery-discovery-view" ref="searchContainer">
    <throbber-widget v-if="status === 'loading'" />
    <!-- Search Bar -->
    <search-bar v-if="showSearchbar" @search="handleSearchInput" />
    <!-- Display View -->
    <div class="discovery-search-results" :style="{ minHeight: (status === 'loading') ? '225px' : '0' }">
      <div v-if="rows > 0" class="discovery-view">
        <search-header v-if="showHeader" :search="search" :displayMode="displayMode" @switchDisplayMode="switchDisplayMode" @switchSortMode="switchSortMode" @setPage="setPage" />
        <masonry-display v-if="displayMode === 'masonry'" :search="search" />
        <rows-display v-if="displayMode === 'rows'" :search="search" />
      </div>
      <div class="discovery-search-message" v-else-if="status === 'waiting'">
        Please enter a search query.
      </div>
      <div v-else-if="status !== 'loading' && search.data.TotalRecordsFound === 0" class="discovery-search-message">
        Sorry, no items were found matching the search criteria.
      </div>
      <div v-else-if="status !== 'loading'" class="discovery-search-message" >
        Oops, something went wrong retrieiving the search results. Please try again.
      </div>
    </div>
  </div>
</template>

<script>
import MasonryDisplay from '@/components/displays/MasonryDisplay.vue';
import RowsDisplay from '@/components/displays/RowsDisplay.vue';
import ThrobberWidget from '@/components/ui/ThrobberWidget.vue';
import SearchHeader from '@/components/ui/SearchHeader.vue';
import SearchBar from '@/components/ui/SearchBar.vue';

export default {
  name: 'SearchResults',
  components: {
    MasonryDisplay,
    RowsDisplay,
    SearchHeader,
    ThrobberWidget,
    SearchBar,
  },
  props: {
    initSearch: {
      type: Object,
      default: null,
    },
    display: {
      type: String,
      default: 'masonry',
    },
    showSearchbar: {
      type: Boolean,
      default: true,
    },
    showHeader: {
      type: Boolean,
      default: true,
    },
    showPager: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      displayMode: this.display ?? localStorage.getItem('discoveryDisplayMode') ?? 'masonry',
      throbber: false,
      multiSearch: true,
      searchType: 'catalog',
      input: '',
      search: {},
      pending: false,
      searchID: null, // Used to abandon searches that return after a newer search has dispatched
      status: 'waiting',
      minHeight: 0,
    };
  },
  mounted() {
    if (this.initSearch !== null) {
      this.dispatchSearch(
        this.initSearch.terms,
        this.initSearch.page,
        this.initSearch.num,
        this.initSearch.sort,
        this.initSearch.orgid,
      );
    }
  },
  computed: {
    rows() {
      return this.search?.data?.PAPIErrorCode ?? 0;
    },
  },
  methods: {
    handleSearchInput(input) {
      this.input = input;
      this.dispatchSearch(`${input.searchBy}=${input.searchString}`);
    },
    dispatchSearch(terms, page = 1, num = 100, sort = 'RELEVANCE', orgid = 1) {
      // Store the search ID for current query
      const id = `${new Date().getTime()}-${crypto.randomUUID()}`;
      Discovery.log(`Dispatching search: ${id}"`);
      // Set state that we are loading a new search
      this.status = 'loading';
      this.pending = {
        id,
        orgid,
        terms,
        page,
        sort,
        num,
        string: this.input.searchString,
      };
      // Make sure page is > 0
      this.pending.page = Math.max(1, this.pending.page);
      // Send to API
      JsPAPI.api.bibSearch(this.pending)
        .then((response) => {
          // If result matches latest dispatched, proceed, otherwise abandon it
          if (id === this.pending.id) {
            Discovery.log(`Received search response: ${id}`);
            // Merge current search settings
            this.search = {
              ...this.pending,
              ...response,
            };
            this.status = 'loaded';
            Discovery.log(this.search);
          } else {
            Discovery.log(`Abandoned search result: ${id}`);
          }
        });
    },
    /**
     * React to display mode changes
     */
    switchDisplayMode(mode) {
      Discovery.log(`switching mode: ${mode}`);
      localStorage.setItem('discoveryDisplayMode', mode);
      this.$root.params.display = mode;
      this.displayMode = mode;
    },
    resetView() {
      this.$refs.searchContainer.scrollIntoView({ behavior: 'smooth' });
    },
    /**
     * React to sort mode changes
     */
    switchSortMode(mode) {
      this.resetView();
      this.dispatchSearch(this.search.terms, 1, this.search.num, mode);
    },
    /**
     * React to page changes
     */
    setPage(page) {
      this.resetView();
      this.dispatchSearch(this.search.terms, page, this.search.num, this.search.sort);
    },
  },
};
</script>

<style lang="less">
@import '~@/css/global';

.discovery-search-results, .discovery-discovery-view {
  position: relative;
}
.discovery-search-message {
  text-align: center;
  padding: 2em;
}
</style>
